import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Banknote, Hourglass, RefreshCcw } from "lucide-react";
import { useAppStore } from "@/hooks/useAppStore";
import { getBalance, purchaseCredits } from "@/services/purchase";
import { DateTime } from "luxon";

const FREE_TOKENS = "t_free";
const PREMIUM_TOKENS = "t_premium";

function getHoursSinceMidnight() {
  //Tokyo time
  const jstEOD = DateTime.now().setZone("Asia/Tokyo").endOf("day");
  const diff = jstEOD.diff(DateTime.now().setZone("Asia/Tokyo"), ["hours", "minute"]);
  return diff.hours <= 1 ? "less than an hour" : (diff.hours == 1 ? "~1 hour" : " about " + diff.hours + " hours");
}

interface TokenBalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TokenBalanceDialog: React.FC<TokenBalanceDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [purchasePending, setPurchasePending] = useState<boolean>(false);

  const balance = useAppStore((state => state.balance));
  const setBalance = useAppStore((state => state.setBalance));
  const fetchBalance = () => {
    setLoading(true);
    getBalance(FREE_TOKENS).then(wallet => {
      setBalance({ free: wallet.balance, premium: 0 });
    }).catch(e => {
      console.error("Error fetching balance:", e);
    }).finally(() => {
      setLoading(false);
    })
  };
  useEffect(() => {
    if (open || purchasePending) {
      fetchBalance();
      setPurchasePending(false);
    }
  }, [open]);

  const onPurchaseClick = (amount: number) => {
    setDisabled(true);
    purchaseCredits(FREE_TOKENS, amount)
      .catch(e => {
        console.error("Error while purchasing credits", e);
      })
      .then(() => {
        setPurchasePending(true);
      })
      .finally(() => {
        setDisabled(false);
      });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center justify-center">
            Credit Balance <Banknote className="inline-flex w-6 ml-2 mt-1" />
          </DialogTitle>
        </DialogHeader>
        <div className="px-2 py-4">
          <div className="text-center py-2">
            <div className="text-lg flex items-center justify-center text-gray-200">
              <div>You have <span className={"font-bold" + (loading ? " text-gray-400" : " text-white")}>{balance.free}</span> credits.</div>
              <button className="ml-2" onClick={(e) => { e.preventDefault(); fetchBalance(); }}>
                <RefreshCcw className={"inline-flex w-4 text-chotto-blue" + (loading ? " animate-spin" : "")} />
              </button>
            </div>
            {balance.free && balance.free < 1000 &&
              <div className="text-sm mt-2 flex items-center justify-center">
                <Hourglass className="h-3 inline-flex animate-spinDelayed" /> Replenishes in {getHoursSinceMidnight()}.</div>}
          </div>
          <div className="grid grid-cols-6 py-2 mt-3 gap-4">
            <div className="text-center text-sm col-span-6">
              Buy more credits:
            </div>
            <Button
              className="bg-chotto-blue hover:bg-chotto-blue/90 col-span-3"
              type="button"
              disabled={disabled}
              variant="secondary"
              size="sm"
              onClick={(e) => { onPurchaseClick(1000); e.preventDefault(); }}>1,000 (¥149)</Button>
            <Button
              className="bg-chotto-blue hover:bg-chotto-blue/90 col-span-3"
              type="button"
              disabled={disabled}
              variant="secondary"
              size="sm"
              onClick={(e) => { onPurchaseClick(5000); e.preventDefault(); }}>5,000 (¥499)</Button>
            <Button
              className="bg-chotto-blue hover:bg-chotto-blue/90 col-span-6"
              type="button"
              disabled={disabled}
              variant="secondary"
              size="sm"
              onClick={(e) => { onPurchaseClick(20000); e.preventDefault(); }}>20,000 (¥1,499)</Button>
          </div>
          <div className="text-center">
            {/* <Button variant="link" size="sm" className="text-color-blue" onClick={() => alert("WIP")}>View Purchase History</Button> */}
          </div>
        </div>
        <DialogFooter className="sm:justify-right px-2 mt-3">
          <Button
            type="button"
            variant="secondary"
            disabled={disabled}
            onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
